/**
 * GenUI bento grid engine — LLM cell order/colSpan; FE owns 12-col + responsive reflow.
 * Callers: InsightsAnswer when tryParseGenuiSpecStreaming returns type:"bento".
 * Schema: InsightsGenuiBento { type:"bento", cols:12, cells[{id,kind,colSpan,payload}] }.
 * Cells use straight border separators (no nested rounded cards).
 */

"use client";

import { GenuiBentoCellView } from "./InsightsGenuiTiles";
import type { GenuiColSpan, InsightsGenuiBento } from "./genui-bento-types";
import { genuiColSpanClass } from "./genui-bento-types";
import { cn } from "@/shared/lib/utils/cn";

/** Matches genuiColSpanClass sm: spans (colSpan 8 caps at 6). */
function smSpan(colSpan: GenuiColSpan): number {
  return colSpan === 8 ? 6 : colSpan;
}

function advanceCursor(cursor: number, span: number): { cursor: number; endRow: boolean } {
  let next = cursor;
  if (next + span > 12) next = 0;
  next += span;
  const endRow = next === 12;
  return { cursor: endRow ? 0 : next, endRow };
}

function BentoSkeletonGhosts() {
  return (
    <>
      <div className="col-span-12 h-16 animate-pulse border-b border-border/40 bg-muted/50" />
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            "col-span-12 h-20 animate-pulse border-b border-border/40 bg-muted/40 sm:col-span-3",
            i < 3 && "sm:border-r",
          )}
        />
      ))}
      <div className="col-span-12 h-40 animate-pulse border-b border-border/40 bg-muted/45" />
      <div className="col-span-12 h-28 animate-pulse border-b sm:border-r border-border/40 bg-muted/40 sm:col-span-6" />
      <div className="col-span-12 h-28 animate-pulse border-b border-border/40 bg-muted/40 sm:col-span-6" />
    </>
  );
}

/**
 * Renders an LLM-designed bento document. Topology comes only from `spec.cells`.
 */
export function InsightsGenuiBento({
  spec,
  live = false,
}: {
  spec: InsightsGenuiBento;
  live?: boolean;
}) {
  const empty = spec.cells.length === 0;

  // Pure fold — avoid reassigning cursors during render (eslint react-hooks/immutability).
  const cellLayout = spec.cells.reduce<{
    sm: number;
    lg: number;
    rows: Array<{
      cell: InsightsGenuiBento["cells"][number];
      smEndRow: boolean;
      lgEndRow: boolean;
    }>;
  }>(
    (acc, cell) => {
      const sm = advanceCursor(acc.sm, smSpan(cell.colSpan));
      const lg = advanceCursor(acc.lg, cell.colSpan);
      return {
        sm: sm.cursor,
        lg: lg.cursor,
        rows: [...acc.rows, { cell, smEndRow: sm.endRow, lgEndRow: lg.endRow }],
      };
    },
    { sm: 0, lg: 0, rows: [] },
  ).rows;

  return (
    <div
      className={cn("grid w-full min-w-0 grid-cols-12 gap-0", live && "border border-primary/25")}
      data-genui="bento"
    >
      {empty && live ? <BentoSkeletonGhosts /> : null}
      {cellLayout.map(({ cell, smEndRow, lgEndRow }) => (
        <div
          key={cell.id}
          className={cn(
            "min-w-0 border-b border-border/40",
            // Mobile stacks full-width — no vertical rules. sm/lg track reflow.
            !smEndRow && "sm:border-r",
            lgEndRow ? "lg:border-r-0" : "lg:border-r",
            genuiColSpanClass(cell.colSpan),
          )}
          data-genui-kind={cell.kind}
          data-genui-span={cell.colSpan}
        >
          <GenuiBentoCellView cell={cell} live={live} />
        </div>
      ))}
    </div>
  );
}
